import styles from "./BlockedTimeWizard.module.scss";
import { PageMode } from "../../../../common/types";
import { useBlockedTimeWizardPage } from "./hooks/useBlockedTimeWizardPage";
import { BlockedTimeWizardMainInputs } from "./components/BlockedTimeWizardMainInputs";
import { RecurringEventSelector } from "../common/recurringEventSelector/RecurringEventSelector";
import { HiddenField } from "../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { IBlockedTimeWizardFormModel } from "./models/blockedTimeWizardFormModel";
import { nameof } from "ts-simple-nameof";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { FormProvider } from "react-hook-form";
import { Tooltip } from "primereact/tooltip";
import { SingleSectionPageLayout } from "../../../../common/components/common/SingleSectionPageLayout";
import { IEventWizardInitialData } from "../models/eventWizardInitialDataModel";
import { IEmployeeWithAvailabilityModel } from "../../../../common/components/forms/inputs/instructorCalendar/models/employeeCalendarModels";
import { useEffect, useState } from "react";
import { EventType } from "../../../../common/enums";
import { DefaultToggle } from "../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";

interface IBlockedTimeWizardProps {
  pageMode: PageMode;
  initialData?: IEventWizardInitialData;
  studioEmployees?: IEmployeeWithAvailabilityModel[];
  isLoading: boolean;
}

export const BlockedTimeWizard = ({
  pageMode,
  initialData,
  studioEmployees,
  isLoading,
}: IBlockedTimeWizardProps) => {
  const {
    form,
    pageTitle,
    buttonTitle,
    dateText,
    breakTypes,
    isBreakTypesLoading,
    isFullDay,
    onFullDayChange,
    onTrySave,
  } = useBlockedTimeWizardPage({
    pageMode,
    initialData,
    studioEmployees,
  });

  const [isRecurringActive, setIsRecurringActive] = useState(false);
  const [hasRecurringChecked, setHasRecurringChecked] = useState(false);
  const [recurringFormValid, setRecurringFormValid] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const handleRecurringStatusChange = (
    isRecurring: boolean,
    hasChecked: boolean,
    isFormValid: boolean,
  ) => {
    setIsRecurringActive(isRecurring);
    setHasRecurringChecked(hasChecked);
    setRecurringFormValid(isFormValid);
    setHasUserInteracted(true);
  };

  // Full-day mode has no recurring check to wait for.
  const isSubmitDisabled =
    !isFullDay &&
    (!hasUserInteracted ||
      (isRecurringActive && !hasRecurringChecked) ||
      (isRecurringActive && !recurringFormValid));

  useEffect(() => {
    form.setValue("type", EventType.BlockedTime);
  }, [form]);

  const titleComponent = (
    <div className={styles.root_title_wrapper}>
      <div className={styles.root_title}>{pageTitle}</div>
      <div className={styles.root_date_text}>{dateText}</div>
    </div>
  );

  const children = (
    <>
      <div className={styles.root_fields}>
        <RenderWhen condition={pageMode !== "edit"}>
          <DefaultToggle
            name={nameof<IBlockedTimeWizardFormModel>((x) => x.isFullDay)}
            label="Блокиране за цял ден"
            tooltip="Блокира служителя от 00:00 до 24:00 за всеки ден в избрания период (напр. отпуск или болничен)."
            onChange={onFullDayChange}
            disabled={isLoading}
          />
        </RenderWhen>

        <BlockedTimeWizardMainInputs
          studioEmployees={studioEmployees}
          breakTypes={breakTypes}
          isLoading={isLoading || isBreakTypesLoading}
          isFullDay={isFullDay}
          pageMode={pageMode}
        />

        <RenderWhen condition={!isFullDay}>
          <RecurringEventSelector
            width={"100%"}
            pageMode={pageMode}
            isRecurringFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.isRecurring,
            )}
            recurringDaysFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.recurringDays,
            )}
            recurringPeriodFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.recurringPeriod,
            )}
            recurringPeriodTypeFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.recurringPeriodType,
            )}
            recurringIntervalFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.recurringInterval,
            )}
            recurringIntervalTypeFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.recurringIntervalType,
            )}
            checkboxLabel="Този блокиран слот ще се потваря ли?"
            onRecurringStatusChange={handleRecurringStatusChange}
            dateFieldName={nameof<IBlockedTimeWizardFormModel>((x) => x.date)}
            durationFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.durationInMinutes,
            )}
            employeeIdFieldName={nameof<IBlockedTimeWizardFormModel>(
              (x) => x.employeeId,
            )}
            studioEmployees={studioEmployees}
          />
        </RenderWhen>

        <HiddenField
          name={nameof<IBlockedTimeWizardFormModel>((x) => x.date)}
        />
        <HiddenField
          name={nameof<IBlockedTimeWizardFormModel>((x) => x.studioId)}
        />
        <HiddenField
          name={nameof<IBlockedTimeWizardFormModel>((x) => x.type)}
        />
      </div>
      <div className={styles.root_buttons}>
        <DefaultButton
          label={buttonTitle}
          type="submit"
          disabled={isSubmitDisabled}
        />
        {isSubmitDisabled && (
          <Tooltip
            target=".disabled-submit-button"
            content="Първо проверете достъпността за повторение"
            position="top"
          />
        )}
      </div>
    </>
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onTrySave)}>
        <SingleSectionPageLayout
          title={titleComponent}
          children={children}
          height={"auto"}
          maxHeight={"80%"}
        />
      </form>
    </FormProvider>
  );
};
